import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Compass, Loader2, ArrowRight, Briefcase, Building2, Upload, Trash2,
  BadgeCheck, MapPin, Phone, Mail, Sparkles, HelpCircle,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import RALayout from "../components/ra/RALayout";
import { API, requestTradeAccess } from "../lib/adventureApi";
import {
  login, register, updateAgency, isAuthed, getRole, getUser, authHeader,
} from "../lib/auth";

const inputCls = "w-full px-3 py-2.5 text-sm bg-white border border-sand-dark rounded-sm text-ink focus:outline-none focus:ring-2 focus:ring-terracotta";
const labelCls = "font-mono text-[10px] uppercase tracking-widest text-moss";

async function uploadLogo(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API}/agency/logo`, { method: "POST", headers: authHeader(), body: fd });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(typeof e.detail === "string" ? e.detail : "Logo upload failed");
  }
  const d = await res.json();
  return `${API}/agency/logo/${d.path}`;
}

function RequestAccessDialog({ open, onClose }) {
  const [f, setF] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (f.name.trim().length < 2 || !f.email.includes("@")) { toast.error("Please add your name and a valid email"); return; }
    setLoading(true);
    try {
      await requestTradeAccess(f);
      toast.success("Request sent", { description: "Our team will be in touch with your login shortly." });
      onClose();
      setF({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (e) { toast.error(e.message || "Could not send request"); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-md" data-testid="request-access-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">Request trade access</DialogTitle></DialogHeader>
        <p className="text-sm text-ink-secondary -mt-2 mb-1">No login yet? Tell us about your agency and we'll set you up. This alerts our team directly.</p>
        <div className="space-y-3">
          <div><label className={labelCls}>Your name</label><input data-testid="req-name" className={`${inputCls} mt-1`} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div><label className={labelCls}>Work email</label><input data-testid="req-email" className={`${inputCls} mt-1`} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Company</label><input data-testid="req-company" className={`${inputCls} mt-1`} value={f.company} onChange={(e) => set("company", e.target.value)} /></div>
            <div><label className={labelCls}>Phone</label><input data-testid="req-phone" className={`${inputCls} mt-1`} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          </div>
          <div><label className={labelCls}>Message (optional)</label><textarea data-testid="req-message" rows={2} className={`${inputCls} mt-1`} value={f.message} onChange={(e) => set("message", e.target.value)} /></div>
          <button data-testid="req-submit" onClick={submit} disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-11 rounded-sm font-semibold tracking-wide transition-colors disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send request <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GenericLogin({ onDone }) {
  const [email, setEmail] = useState("trade@wayfinder.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [reqOpen, setReqOpen] = useState(false);
  const submit = async () => {
    setErr(""); setLoading(true);
    try { const u = await login(email, password); toast.success(`Welcome, ${u.name || "partner"}`); onDone(); }
    catch (e) { setErr(e.message || "Login failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="max-w-md mx-auto px-5 py-16" data-testid="trade-login">
      <div className="flex items-center gap-2 mb-1 text-moss"><Briefcase className="w-4 h-4" /><span className="font-mono text-xs uppercase tracking-widest">Trade access</span></div>
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-2">Sign in to the trade portal</h1>
      <p className="text-sm text-ink-secondary mb-6">Use the shared trade login to build client itineraries with live pricing and your commission. Want your own logo on proposals? You'll set that up next.</p>
      <label className={labelCls}>Work email</label>
      <input data-testid="trade-email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-1 mb-4`} />
      <label className={labelCls}>Password</label>
      <input data-testid="trade-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} className={`${inputCls} mt-1 mb-4`} />
      {err && <p className="text-sm text-destructive mb-3" data-testid="trade-error">{err}</p>}
      <button data-testid="trade-login-btn" onClick={submit} disabled={loading}
        className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-12 rounded-sm font-semibold tracking-wide transition-colors disabled:opacity-50">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Enter portal <ArrowRight className="w-4 h-4" /></>}
      </button>
      <button data-testid="open-request-access" onClick={() => setReqOpen(true)}
        className="w-full mt-4 flex items-center justify-center gap-1.5 text-sm text-ink-muted hover:text-terracotta transition-colors">
        <HelpCircle className="w-4 h-4" /> Don't have a login? Request access
      </button>
      <RequestAccessDialog open={reqOpen} onClose={() => setReqOpen(false)} />
    </div>
  );
}

function AgencySetup({ onDone }) {
  const [f, setF] = useState({ email: "", password: "", company_name: "", contact_name: "", phone: "", company_email: "", address: "", logo_url: "" });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5MB"); return; }
    setUploading(true);
    try { const url = await uploadLogo(file); set("logo_url", url); toast.success("Logo uploaded"); }
    catch (e2) { toast.error(e2.message || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    setErr("");
    if (!f.email.includes("@")) { setErr("Enter a valid work email"); return; }
    if (f.password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (!f.company_name.trim() || !f.contact_name.trim() || !f.phone.trim() || !f.address.trim()) { setErr("Company, contact, phone and address are required"); return; }
    setLoading(true);
    try {
      await register(f);
      toast.success("Your branded agency account is ready");
      onDone();
    } catch (e) { setErr(e.message || "Registration failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-5 py-14" data-testid="agency-setup">
      <div className="flex items-center gap-2 mb-1 text-moss"><Building2 className="w-4 h-4" /><span className="font-mono text-xs uppercase tracking-widest">Step 2 · White-label</span></div>
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-2">Set up your branded agency</h1>
      <p className="text-sm text-ink-secondary mb-6">Create your permanent login. Your logo and details will appear on every proposal you generate — instead of Responsible Adventures branding.</p>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Company name</label><input data-testid="agency-company" className={`${inputCls} mt-1`} value={f.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Summit & Silk Travel" /></div>
          <div><label className={labelCls}>Contact person</label><input data-testid="agency-contact" className={`${inputCls} mt-1`} value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Phone</label><input data-testid="agency-phone" className={`${inputCls} mt-1`} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          <div><label className={labelCls}>Public email (on proposals)</label><input data-testid="agency-public-email" className={`${inputCls} mt-1`} value={f.company_email} onChange={(e) => set("company_email", e.target.value)} placeholder="hello@youragency.com" /></div>
        </div>
        <div><label className={labelCls}>Full address</label><textarea data-testid="agency-address" rows={2} className={`${inputCls} mt-1`} value={f.address} onChange={(e) => set("address", e.target.value)} /></div>

        <div>
          <label className={labelCls}>Company logo (PNG / JPG / SVG, max 5MB)</label>
          <div className="flex items-center gap-3 mt-1">
            {f.logo_url ? (
              <div className="relative">
                <img src={f.logo_url} alt="logo" className="h-14 w-auto max-w-[160px] object-contain bg-white border border-sand-dark rounded-sm p-1" />
                <button data-testid="agency-logo-remove" onClick={() => set("logo_url", "")} className="absolute -top-2 -right-2 w-5 h-5 bg-ink text-white rounded-full flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
              </div>
            ) : (
              <label className="flex items-center gap-2 px-3 py-2 rounded-sm border border-dashed border-sand-dark text-sm text-ink-secondary cursor-pointer hover:border-moss transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? "Uploading…" : "Upload logo"}
                <input data-testid="agency-logo-input" type="file" accept="image/*" className="hidden" onChange={onLogo} />
              </label>
            )}
          </div>
        </div>

        <div className="pt-2 border-t border-sand-dark grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Login email</label><input data-testid="agency-login-email" className={`${inputCls} mt-1`} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
          <div><label className={labelCls}>Password</label><input data-testid="agency-login-password" type="password" className={`${inputCls} mt-1`} value={f.password} onChange={(e) => set("password", e.target.value)} /></div>
        </div>

        {err && <p className="text-sm text-destructive" data-testid="agency-setup-error">{err}</p>}
        <button data-testid="agency-create-btn" onClick={submit} disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-12 rounded-sm font-semibold tracking-wide transition-colors disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create branded account <Sparkles className="w-4 h-4" /></>}
        </button>
      </div>
    </div>
  );
}

function AgencyDashboard() {
  const navigate = useNavigate();
  const u = getUser() || {};
  return (
    <div className="max-w-lg mx-auto px-5 py-16" data-testid="agency-dashboard">
      <div className="flex items-center gap-2 mb-3 text-moss"><BadgeCheck className="w-5 h-5" /><span className="font-mono text-xs uppercase tracking-widest">Branded partner</span></div>
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-6">You're all set, {u.contact_name || u.name}</h1>
      <div className="border border-sand-dark rounded-md bg-white p-6">
        <div className="flex items-center gap-4 mb-5">
          {u.logo_url ? <img src={u.logo_url} alt="logo" className="h-14 w-auto max-w-[160px] object-contain" /> : <div className="w-14 h-14 rounded-sm bg-sand flex items-center justify-center"><Building2 className="w-6 h-6 text-ink-muted" /></div>}
          <div><p className="font-display font-bold text-lg tracking-tight">{u.company_name}</p><p className="text-sm text-ink-muted">Proposals will carry this branding</p></div>
        </div>
        <ul className="space-y-2 text-sm text-ink-secondary">
          {u.company_email && <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-terracotta" />{u.company_email}</li>}
          {u.phone && <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-terracotta" />{u.phone}</li>}
          {u.address && <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-terracotta mt-0.5" />{u.address}</li>}
        </ul>
      </div>
      <button data-testid="agency-go-builder" onClick={() => navigate("/build")}
        className="w-full mt-5 flex items-center justify-center gap-2 bg-moss hover:bg-moss-hover text-white h-12 rounded-sm font-semibold tracking-wide transition-colors">
        Open the map builder <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function TradeAccess() {
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((t) => t + 1);
  const authed = isAuthed();
  const role = getRole();

  let body;
  if (!authed) body = <GenericLogin onDone={refresh} />;
  else if (role === "agency") body = <AgencyDashboard />;
  else body = <AgencySetup onDone={refresh} />; // generic trade / admin → offer branding setup

  return <RALayout key={tick}>{body}</RALayout>;
}
